import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Role } from 'Src/models/roles';
import {
  PageHelpAction,
  PageHelpAnchor,
  PageHelpDefinition,
  PageHelpFilter,
  PageHelpObservation,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';

type Audience = 'parent' | 'student';

interface Props {
  audience: Audience;
  pathname: string;
  user: AppUser;
  availableRoles: Role[];
  history: any;
}

interface RouteHelp {
  key: string;
  title: string;
  entity: { singular: string; plural: string };
  subtitle: string;
  summary: string;
  items: string[];
  availability?: 'awards' | 'absence' | 'availability';
}

const ROUTE_HELP: Array<{ matches: (path: string, audience: Audience) => boolean; value: RouteHelp }> = [
  {
    matches: path => path.indexOf('/home') === 0,
    value: {
      key: 'home',
      title: 'Home',
      entity: { singular: 'update', plural: 'updates' },
      subtitle: 'A live summary of what needs your attention across this account.',
      summary:
        'Home combines upcoming activity and outstanding actions. A badge is a shortcut to the live list, not a separate copy of the data.',
      items: [
        'Open an item to see its current details before responding.',
        'If a count changes after a deadline or event start, refresh the destination list so the latest eligibility rules are applied.',
        'Features shown here depend on the modules enabled by the relevant school.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/calendar') === 0,
    value: {
      key: 'calendar',
      title: 'Calendar',
      entity: { singular: 'event', plural: 'events' },
      subtitle: 'Squady follows the selected date, person and activity filters.',
      summary:
        'The calendar shows events available to the active role. Parent results can combine children from different schools.',
      items: [
        'Choose a child to narrow a parent calendar; choose All children to combine them.',
        'Open an event for participant, consent, availability, job, result and recognition details that are available to this role.',
        'An action can disappear after an event starts or when the school has not enabled it for this role.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/children') === 0,
    value: {
      key: 'children',
      title: 'Children',
      entity: { singular: 'child', plural: 'children' },
      subtitle: 'Linked child profiles, their schools and shortcuts into filtered activity.',
      summary:
        'Each card belongs to a child linked to this parent role. School-specific features can differ between children.',
      items: [
        'Calendar, Awards and Consents shortcuts open the matching list already filtered to that child.',
        'Award and consent counts use current eligibility rules and may update after deadlines or event starts.',
        'School, form and house appear only when the school has supplied those details.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/consentRequests') === 0,
    value: {
      key: 'consent-requests',
      title: 'Consent requests',
      entity: { singular: 'request', plural: 'requests' },
      subtitle: 'Squady checks the active academic-year view and whether a response is still allowed.',
      summary:
        'Current contains only requests that can still be answered. Older or no-longer-actionable requests move to the appropriate past view.',
      items: [
        'A configured response deadline always takes priority.',
        'Without a deadline, consent can be submitted until the event starts.',
        'Opening a past request is safe, but its previous response cannot be changed when the action window has closed.'
      ]
    }
  },
  {
    matches: path => path === '/clubs',
    value: {
      key: 'clubs',
      title: 'Clubs',
      entity: { singular: 'club option', plural: 'club options' },
      subtitle: 'Invitations, available programmes and bookings for the active role.',
      summary: 'This hub separates invitations that need a response from programmes that can be browsed or booked.',
      items: [
        'Open Club invitations to review current and previous invitations.',
        'Open Browse club programmes to see activities available to the linked child or student.',
        'Availability can differ by school, participant, booking window and remaining capacity.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/clubBookings') === 0,
    value: {
      key: 'club-invitations',
      title: 'Club invitations',
      entity: { singular: 'invitation', plural: 'invitations' },
      subtitle: 'Invitations, booking windows and past responses for this role.',
      summary:
        'An invitation stays current until its response deadline. Without one, the club schedule determines when booking closes.',
      items: [
        'For fixed runs, the club finish date closes the invitation.',
        'For term-based clubs, the last included term end is used; for specific dates, the last activity date is used.',
        'Squady can explain Accept or Decline, but never submits either response without your confirmation.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/clubProgrammes') === 0,
    value: {
      key: 'club-programmes',
      title: 'Browse club programmes',
      entity: { singular: 'programme', plural: 'programmes' },
      subtitle: 'Programme availability, booking windows and eligible participants.',
      summary:
        'Programmes appear only for the schools and students linked to this role and may expose different clubs or booking forms.',
      items: [
        'A booking may be unavailable before opening, after closing, when capacity is exhausted or when the participant is not eligible.',
        'Choose another eligible child when a parent programme offers places to more than one linked student.',
        'Submitting or paying for a booking always remains an explicit user action.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/availabilityReports') === 0,
    value: {
      key: 'availability-reports',
      title: 'Availability reports',
      entity: { singular: 'report', plural: 'reports' },
      subtitle: 'Current-year reports, older history and the availability actions permitted by each event.',
      summary:
        'Reports are split by academic year. A report can be created only while the event and role still permit availability reporting.',
      items: [
        'Use the event page to submit or update availability when Report availability is offered.',
        'The action is unavailable after the event starts or when the school/event settings do not permit this role to report.',
        'Parent lists can include reports sent by a linked student; the sender identifies who submitted each report.'
      ],
      availability: 'availability'
    }
  },
  {
    matches: (path, audience) => audience === 'parent' && path.indexOf('/absenceRequests') === 0,
    value: {
      key: 'absence-requests',
      title: 'Absence requests',
      entity: { singular: 'request', plural: 'requests' },
      subtitle: 'Notifications sent to a child’s school about an event or activity absence.',
      summary:
        'Parents see this feature when at least one linked child belongs to a school with absence requests enabled.',
      items: [
        'Choose the child first so the request is sent to the correct school.',
        'If New absence request is unavailable for one child, another linked school may still support it.',
        'Archive is read-only and keeps previously submitted notifications.'
      ],
      availability: 'absence'
    }
  },
  {
    matches: path => path.indexOf('/awards') === 0,
    value: {
      key: 'awards',
      title: 'Awards & House Points',
      entity: { singular: 'award', plural: 'awards' },
      subtitle: 'Recognition and house points from the current academic year.',
      summary:
        'Awards are visible only when the relevant school has the Awards module enabled. Parent results can span multiple linked schools.',
      items: [
        'Select a child to narrow the list or use All children to combine awards.',
        'Open a card for the full reason, awarding member of staff and any attached media.',
        'Awards are granted by the school; parent and student roles can view but cannot issue them.'
      ],
      availability: 'awards'
    }
  },
  {
    matches: path => path.indexOf('/events') === 0,
    value: {
      key: 'event',
      title: 'Event',
      entity: { singular: 'event detail', plural: 'event details' },
      subtitle: 'Squady follows this event’s timing, participants and role-specific actions.',
      summary:
        'The event page exposes only information and tabs that contain relevant data or are available to the active role.',
      items: [
        'Report availability is offered only before the start and only when the role is allowed to submit it.',
        'Consent appears for a parent when a linked child has a consent request for this event.',
        'Recognition appears only when the student or a linked child has an award in this event.',
        'Results, jobs and media appear only when corresponding data exists or the role can use the action.'
      ],
      availability: 'availability'
    }
  },
  {
    matches: path => path.indexOf('/messages') === 0,
    value: {
      key: 'messages',
      title: 'Messages',
      entity: { singular: 'message', plural: 'messages' },
      subtitle: 'Messages available to the active parent or student role.',
      summary:
        'Inbox and sent/archive views are role-scoped. Opening a message does not change school records unless a form is explicitly submitted.',
      items: [
        'Use search and filters to locate a sender, subject or date.',
        'Attachments and reply controls appear only when supplied and permitted.',
        'Check the active role if an expected school message is missing.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/profile') === 0,
    value: {
      key: 'profile',
      title: 'Profile',
      entity: { singular: 'profile detail', plural: 'profile details' },
      subtitle: 'Personal details, linked roles, schools, sports and students.',
      summary: 'Profile roles control which school and role contexts can be selected at login.',
      items: [
        'Parent role details list linked children; coach and teacher details list assigned sports.',
        'Revoking a role removes access to that school context.',
        'To regain a revoked role later, request access again and wait for the school to approve it.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/basket') === 0,
    value: {
      key: 'basket',
      title: 'Basket',
      entity: { singular: 'booking', plural: 'bookings' },
      subtitle: 'Club bookings waiting for review, submission or payment.',
      summary:
        'Items remain associated with the selected child and school. Review prices, forms and participant details before checkout.',
      items: [
        'Removing or paying for an item changes the booking and always requires your explicit action.',
        'A place can become unavailable if capacity or the booking window changes before checkout.',
        'Return to Clubs to choose a different activity or participant.'
      ]
    }
  },
  {
    matches: (path, audience) => audience === 'parent' && path.indexOf('/paymentSuccess') === 0,
    value: {
      key: 'payment-success',
      title: 'Payment complete',
      entity: { singular: 'payment detail', plural: 'payment details' },
      subtitle: 'Confirmation of the club booking payment just completed.',
      summary:
        'This confirmation means the payment flow has returned successfully. The associated booking remains linked to the selected child and school.',
      items: [
        'Use the available navigation to return to Clubs or the relevant booking list.',
        'Do not repeat payment from the browser Back button; review the booking status first.',
        'If the booking does not update, refresh its list before contacting the school.'
      ]
    }
  },
  {
    matches: path => path.indexOf('/tournament/invite') === 0,
    value: {
      key: 'tournament-invitation',
      title: 'Tournament invitation',
      entity: { singular: 'invitation', plural: 'invitations' },
      subtitle: 'Tournament participation information available to this role.',
      summary:
        'The organiser and school control tournament entry. Review the invitation details and any deadline before responding.',
      items: [
        'A closed deadline prevents a new response.',
        'Squady will not accept, decline or withdraw on your behalf.'
      ]
    }
  }
];

const FALLBACK_HELP: RouteHelp = {
  key: 'page',
  title: 'This page',
  entity: { singular: 'item', plural: 'items' },
  subtitle: 'Live help for the current parent or student screen.',
  summary:
    'Squady reads the visible state of this page, including active controls, filters, results and blocking messages.',
  items: [
    'Only actions available to the active role are shown.',
    'Disabled actions normally need a different status, date, school setting or role permission.',
    'Squady never performs a destructive or submission action automatically.'
  ]
};

function isVisible(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
}

function firstVisible(selector: string): HTMLElement | null {
  return (Array.from(document.querySelectorAll(selector)) as HTMLElement[]).find(isVisible) || null;
}

function visibleText(element: Element | null): string {
  return String(element && element.textContent ? element.textContent : '')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveRoute(pathname: string, audience: Audience): RouteHelp {
  const route = ROUTE_HELP.find(candidate => candidate.matches(pathname, audience));
  return route ? route.value : FALLBACK_HELP;
}

function getViewKey(pathname: string): string {
  if (/\/archive(?:\/|$)/.test(pathname)) return 'previous-years';
  if (/\/(history|past)(?:\/|$)/.test(pathname)) return 'past-this-year';
  if (/\/(inbox|outbox)(?:\/|$)/.test(pathname)) return 'current';
  return 'main';
}

function getPermissions(availableRoles: Role[], audience: Audience): any[] {
  const role = (availableRoles || []).find(item => String(item.name).toUpperCase() === audience.toUpperCase());
  return role && Array.isArray(role.permissions) ? role.permissions : [];
}

function isAwardsEnabled(permissions: any[], user: AppUser): boolean {
  const allPermissions = permissions.length ? permissions : [user && user.activePermission].filter(Boolean);
  return allPermissions.some(permission => {
    const subscriptions = (permission && permission.school && permission.school.moduleSubscriptions) || [];
    const awards = subscriptions.find(subscription => subscription.module === 'AWARDS');
    return Boolean(awards && (awards.status === 'ACTIVE' || awards.status === 'TRIAL'));
  });
}

function isAbsenceEnabled(permissions: any[], user: AppUser): boolean {
  const allPermissions = permissions.length ? permissions : [user && user.activePermission].filter(Boolean);
  return allPermissions.some(permission =>
    Boolean(permission && permission.school && permission.school.isAbsenceRequestEnabled)
  );
}

function getPagination(): { currentPage?: number; totalPages?: number } {
  const text = visibleText(firstVisible('.bPagination--modern .bPagination__info'));
  const match = text.match(/Page\s+(\d+)\s+of\s+(\d+)/i);
  return match ? { currentPage: Number(match[1]), totalPages: Number(match[2]) } : {};
}

function getFilters(pathname: string): PageHelpFilter[] {
  const params = new URLSearchParams(window.location.search || '');
  const filters: PageHelpFilter[] = [];
  params.forEach((value, key) => {
    if (key === 'page' || key === 'order' || !value) return;
    filters.push({ id: `${key}-${filters.length}`, label: key.replace(/([a-z])([A-Z])/g, '$1 $2'), value });
  });
  const filterSurface = firstVisible(
    '.parentFilterSurface, .parentFilterDrawer, .awardsFilterPanel, .ParentFilterCard'
  );
  if (filterSurface && filters.length === 0) {
    const values = (Array.from(filterSurface.querySelectorAll('input, select')) as Array<
      HTMLInputElement | HTMLSelectElement
    >)
      .map(field => String(field.value || '').trim())
      .filter(Boolean);
    values.slice(0, 4).forEach((value, index) => filters.push({ id: `visible-${index}`, label: 'Filter', value }));
  }
  return filters;
}

function findButton(label: RegExp): HTMLButtonElement | null {
  return (
    (Array.from(document.querySelectorAll('button')) as HTMLButtonElement[]).find(button => {
      if (!isVisible(button) || button.closest('.squadyDrawer')) return false;
      return label.test(visibleText(button));
    }) || null
  );
}

function buttonAction(id: string, label: string, matcher: RegExp, description: string): PageHelpAction | null {
  const button = findButton(matcher);
  if (!button) return null;
  return {
    id,
    label,
    description: button.disabled ? `${description} This control is currently disabled.` : description,
    disabled: button.disabled,
    run: () => button.click()
  };
}

function elementAction(id: string, label: string, selector: string, description: string): PageHelpAction | null {
  const element = firstVisible(selector) as HTMLButtonElement | null;
  if (!element) return null;
  return {
    id,
    label,
    description: element.disabled ? `${description} This control is currently disabled.` : description,
    disabled: element.disabled,
    run: () => element.click()
  };
}

function readHomeItems(selector: string): Array<{ title: string; detail: string }> {
  return (Array.from(document.querySelectorAll(selector)) as HTMLElement[])
    .filter(isVisible)
    .map(element => ({
      title: visibleText(element.querySelector(':scope > span > strong') || element.querySelector('strong')),
      detail: visibleText(element.querySelector(':scope > span > small') || element.querySelector('small'))
    }))
    .filter(item => Boolean(item.title));
}

function buildHomeObservations(audience: Audience): PageHelpObservation[] {
  const isStudent = audience === 'student';
  const attentionItems = readHomeItems(
    isStudent ? '.studentHome__actionList > button' : '.parentHome__actionsList > button'
  );
  const eventItems = readHomeItems(isStudent ? '.studentHome__eventList > button' : '.parentHome__events > button');
  const observations: PageHelpObservation[] = [];

  if (attentionItems.length > 0) {
    observations.push({
      id: 'home-attention-required',
      tone: 'warning',
      title: `${attentionItems.length} ${attentionItems.length === 1 ? 'item needs' : 'items need'} your attention`,
      text: attentionItems.map(item => `${item.title}: ${item.detail}`).join(' ')
    });
  } else {
    observations.push({
      id: 'home-all-caught-up',
      tone: 'positive',
      title: 'No replies are waiting for you',
      text: isStudent
        ? 'There are currently no club invitations or availability requests that need your response.'
        : 'There are currently no consent requests or club invitations that need your response.'
    });
  }

  if (eventItems.length > 0) {
    const [nextEvent] = eventItems;
    const remaining = eventItems.length - 1;
    observations.push({
      id: 'home-next-event',
      tone: 'neutral',
      title: `Next event: ${nextEvent.title}`,
      text: `${nextEvent.detail || 'Open the event to review its time and details.'}${
        remaining > 0
          ? ` ${remaining} more upcoming ${remaining === 1 ? 'event is' : 'events are'} shown below it.`
          : ''
      }`
    });
  } else {
    observations.push({
      id: 'home-no-events',
      tone: 'neutral',
      title: 'No upcoming events are currently published',
      text: 'New fixtures, clubs and tournament events will appear here when the school publishes them.'
    });
  }

  if (isStudent) {
    const awards = readHomeItems('.studentHome__awardList > div');
    if (awards.length > 0) {
      observations.push({
        id: 'home-recent-recognition',
        tone: 'positive',
        title: `Recent recognition: ${awards.map(award => award.title).join(', ')}`,
        text: 'These are the latest awards shown on Home. Open Awards & House Points for the full current-year list.'
      });
    }
  } else {
    const updates = (Array.from(document.querySelectorAll('.parentHome__updates > button')) as HTMLElement[])
      .filter(isVisible)
      .map(button => `${visibleText(button.querySelector('span'))}: ${visibleText(button.querySelector('strong'))}`)
      .filter(value => !/:\s*$/.test(value));
    if (updates.length > 0) {
      observations.push({
        id: 'home-recent-updates',
        tone: 'neutral',
        title: 'Previously submitted requests',
        text: `${updates.join('; ')}. These are records to review, not necessarily actions waiting for you.`
      });
    }
  }

  return observations;
}

function buildHomeActions(audience: Audience): PageHelpAction[] {
  const isStudent = audience === 'student';
  const actions: PageHelpAction[] = [];
  const attentionSelector = isStudent ? '.studentHome__actionList > button' : '.parentHome__actionsList > button';

  (Array.from(document.querySelectorAll(attentionSelector)) as HTMLButtonElement[])
    .filter(isVisible)
    .forEach((button, index) => {
      const title = visibleText(button.querySelector('strong')) || `Item ${index + 1}`;
      const detail = visibleText(button.querySelector('small'));
      actions.push({
        id: `home-review-${index}`,
        label: `Review ${title.toLowerCase()}`,
        description: detail || 'Open the current items that may need your response.',
        run: () => button.click()
      });
    });

  const calendarAction = elementAction(
    'home-open-calendar',
    'Open calendar',
    isStudent
      ? '[data-help-anchor="student-home-events"] .studentHome__sectionHeader button'
      : '[data-help-anchor="parent-home-events"] .parentHome__sectionHeader button',
    'See the complete calendar, including events beyond this short Home preview.'
  );
  if (calendarAction) actions.push(calendarAction);

  if (isStudent) {
    const awardsAction = elementAction(
      'home-open-awards',
      'Open all awards',
      '[data-help-anchor="student-home-awards"] .studentHome__sectionHeader button',
      'See the complete current academic-year Awards & House Points list.'
    );
    if (awardsAction) actions.push(awardsAction);
  } else {
    const childrenAction = elementAction(
      'home-open-children',
      'Open children',
      '[data-help-anchor="parent-home-children"] .parentHome__children button',
      'Open the linked children list and its child-specific shortcuts.'
    );
    if (childrenAction) actions.push(childrenAction);
  }

  return actions;
}

function buildHomeAnchors(audience: Audience): PageHelpAnchor[] {
  return audience === 'student'
    ? [
        {
          id: 'student-home-attention',
          label: 'Needs your attention',
          description: 'Only current invitations and availability requests that may need your response.',
          selector: '[data-help-anchor="student-home-attention"]'
        },
        {
          id: 'student-home-events',
          label: 'Coming up',
          description: 'A short preview of your next published events; open Calendar for the complete schedule.',
          selector: '[data-help-anchor="student-home-events"]'
        },
        {
          id: 'student-home-profile',
          label: 'Your school context',
          description: 'The student, school, form, house and current house-points context used by this page.',
          selector: '[data-help-anchor="student-home-profile"]'
        },
        {
          id: 'student-home-awards',
          label: 'Recent awards',
          description: 'Your latest recognition only; the Awards page contains the complete current-year list.',
          selector: '[data-help-anchor="student-home-awards"]'
        }
      ]
    : [
        {
          id: 'parent-home-attention',
          label: 'Needs your attention',
          description: 'Current consent requests and club invitations that may need a parent response.',
          selector: '[data-help-anchor="parent-home-attention"]'
        },
        {
          id: 'parent-home-events',
          label: 'Next events',
          description: 'A short family preview; open Calendar for the complete schedule.',
          selector: '[data-help-anchor="parent-home-events"]'
        },
        {
          id: 'parent-home-children',
          label: 'Your children',
          description: 'Children linked to this parent account and shortcuts to their profiles.',
          selector: '[data-help-anchor="parent-home-children"]'
        },
        {
          id: 'parent-home-updates',
          label: 'Recent updates',
          description: 'Submitted availability and absence records; these counts do not necessarily require action.',
          selector: '[data-help-anchor="parent-home-updates"]'
        }
      ];
}

function getActiveCalendarFilter(): string {
  const desktopFilter = visibleText(firstVisible('.cal-filters .cal-pill--on'));
  if (desktopFilter) return desktopFilter;
  const mobileFilter = firstVisible('.cal-filters .cal-select') as HTMLSelectElement | null;
  return mobileFilter && mobileFilter.selectedOptions.length > 0
    ? visibleText(mobileFilter.selectedOptions[0])
    : 'All events';
}

function buildCalendarObservations(audience: Audience): PageHelpObservation[] {
  const date = visibleText(firstVisible('.cal-day__date'));
  const relativeDate = visibleText(firstVisible('.cal-day__rel')).replace(/^·\s*/, '');
  const filter = getActiveCalendarFilter();
  const eventButtons = (Array.from(document.querySelectorAll('.cal-events .cal-ev')) as HTMLElement[]).filter(
    isVisible
  );

  if (eventButtons.length === 0) {
    const participant = audience === 'student' ? 'you' : 'your children';
    const inclusion = audience === 'student' ? 'includes you' : 'includes one of your children';
    return [
      {
        id: 'calendar-selected-day-empty',
        tone: 'neutral',
        title: `No events for ${participant} on ${date || 'the selected day'}`,
        text: `Events appear here when the school ${inclusion} in a fixture, club, tournament event or other activity. Choose a day with coloured dots in the month grid to see its events. You can use the type filters to show all events, fixtures, clubs or tournament events.${
          filter && filter !== 'All events' ? ` The active filter is “${filter}”.` : ''
        }`
      }
    ];
  }

  const eventDetails = eventButtons.slice(0, 3).map(button => {
    const time = visibleText(button.querySelector('.cal-ev__time'));
    const name = visibleText(button.querySelector('.cal-ev__name'));
    const meta = visibleText(button.querySelector('.cal-ev__meta'));
    const status = visibleText(button.querySelector('.cal-badge'));
    return `${time} — ${name}${meta ? ` (${meta}${status ? `; ${status}` : ''})` : status ? ` (${status})` : ''}`;
  });
  const remaining = eventButtons.length - eventDetails.length;

  return [
    {
      id: 'calendar-selected-day',
      tone: 'positive',
      title: `${eventButtons.length} ${eventButtons.length === 1 ? 'event' : 'events'} on ${date ||
        'the selected day'}`,
      text: `${
        relativeDate === 'Today' ? 'Today means this is the currently selected calendar date. ' : ''
      }${eventDetails.join(' ') || 'Open an event to review its details.'}${
        remaining > 0 ? ` ${remaining} more ${remaining === 1 ? 'event is' : 'events are'} shown in the list.` : ''
      }`
    },
    {
      id: 'calendar-active-filter',
      tone: 'neutral',
      title: `Showing: ${filter || 'All events'}`,
      text: 'The event count applies only to the selected date, school or child selection, and this event-type filter.'
    }
  ];
}

function buildCalendarActions(): PageHelpAction[] {
  const firstEvent = firstVisible('.cal-events .cal-ev') as HTMLButtonElement | null;
  if (!firstEvent) return [];
  const eventName = visibleText(firstEvent.querySelector('.cal-ev__name')) || 'event';
  return [
    {
      id: 'calendar-open-first-event',
      label: `Open ${eventName}`,
      description: 'Open the first event shown for the selected day. This does not submit or change anything.',
      run: () => firstEvent.click()
    }
  ];
}

function buildCalendarAnchors(audience: Audience): PageHelpAnchor[] {
  return [
    {
      id: 'calendar-context-selection',
      label: audience === 'student' ? 'School selection' : 'Child selection',
      description:
        audience === 'student'
          ? 'Choose one linked school or combine all available school calendars.'
          : 'Choose one child or combine all linked children in the family calendar.',
      selector: audience === 'student' ? '.bItemsBar' : '.pcal-kids, .bItemsBar__mobileWrapper'
    },
    {
      id: 'calendar-filters',
      label: 'Event type filter',
      description: 'Show all events, fixtures, clubs or tournament events for the selected day.',
      selector: '.cal-filters'
    },
    {
      id: 'calendar-month',
      label: 'Month calendar',
      description: 'Choose a date. Markers indicate which kinds of events are published on each day.',
      selector: '.cal-card--month'
    },
    {
      id: 'calendar-selected-day',
      label: 'Selected day and events',
      description: 'The full date, whether it is today, and every matching event for that selected date.',
      selector: '.cal-card--events'
    }
  ];
}

function getClubInvitationCards(): HTMLElement[] {
  return (Array.from(document.querySelectorAll('.bClubBookingCard')) as HTMLElement[]).filter(isVisible);
}

function getClubCardSummary(card: HTMLElement): string {
  const name = visibleText(card.querySelector('.ConsentCard__title')) || 'Club invitation';
  const status = visibleText(card.querySelector('.ConsentCard__status'));
  const deadline = visibleText(card.querySelector('.clubInviteCard__deadline strong'));
  const closed = visibleText(card.querySelector('.ConsentCard__deadlineBanner'));
  return `${name}${status ? ` — ${status}` : ''}${deadline ? `; reply by ${deadline}` : closed ? `; ${closed}` : ''}`;
}

function buildClubInvitationObservations(
  view: string,
  audience: Audience,
  hasAppliedArchiveFilters: boolean
): PageHelpObservation[] {
  const cards = getClubInvitationCards();
  const summaries = cards.slice(0, 3).map(getClubCardSummary);
  const remaining = cards.length - summaries.length;

  if (view === 'current') {
    if (cards.length === 0) {
      return [
        {
          id: 'club-inbox-empty',
          tone: 'positive',
          title: 'Your club invitation Inbox is clear',
          text:
            'There are no club invitations waiting for a response. If you recently accepted or declined one and need to review or change it, open Past this year.'
        }
      ];
    }

    return [
      {
        id: 'club-inbox-items',
        tone: 'warning',
        title: `${cards.length} ${cards.length === 1 ? 'invitation is' : 'invitations are'} shown in Inbox`,
        text: `${summaries.join(' ')}${remaining > 0 ? ` ${remaining} more are shown on this page.` : ''}`
      },
      {
        id: 'club-inbox-actions',
        tone: 'neutral',
        title: 'These invitations are still waiting for your decision',
        text:
          'Use Book or Info to review the booking form before confirming, or Decline to reject an invitation. Recently answered invitations move to Past this year.'
      }
    ];
  }

  if (view === 'past-this-year') {
    const editButtons = (Array.from(
      document.querySelectorAll('.bClubBookingCard .clubInviteCard__edit')
    ) as HTMLButtonElement[]).filter(isVisible);
    const returnButtons = (Array.from(document.querySelectorAll('.bClubBookingCard button')) as HTMLButtonElement[])
      .filter(isVisible)
      .filter(button => /^Return to inbox$/i.test(visibleText(button)));
    const changeCount = editButtons.length + returnButtons.length;
    const observations: PageHelpObservation[] = [
      {
        id: 'club-past-current-purpose',
        tone: 'neutral',
        title: cards.length
          ? `${cards.length} recent ${cards.length === 1 ? 'response is' : 'responses are'} shown`
          : 'No club responses are shown for this academic year',
        text: cards.length
          ? `${summaries.join(' ')}${remaining > 0 ? ` ${remaining} more are shown on this page.` : ''}`
          : 'Recently accepted or declined invitations appear here. Clear or adjust the filters if you expected to find one.'
      }
    ];

    if (changeCount > 0) {
      observations.push({
        id: 'club-past-current-editable',
        tone: 'positive',
        title: `${changeCount} ${changeCount === 1 ? 'response can' : 'responses can'} still be changed`,
        text:
          audience === 'student'
            ? 'Use Return to inbox on a declined invitation, then open Invitations and answer it again.'
            : 'Use Edit response on an accepted invitation, or Return to inbox on a declined invitation, while that button remains available.'
      });
    } else if (cards.length > 0) {
      observations.push({
        id: 'club-past-current-read-only',
        tone: 'warning',
        title: 'The responses shown here cannot be changed online',
        text:
          'If a card has no Edit response or Return to inbox button, its response is read-only here. The response window has usually closed; paid-booking or role rules can also prevent editing. Contact the school directly if an important correction is needed.'
      });
    }

    return observations;
  }

  if (!hasAppliedArchiveFilters) {
    return [
      {
        id: 'club-previous-filter-required',
        tone: 'warning',
        title: 'Set filters to search previous academic years',
        text:
          'This long-term archive is filter-led so the app does not download years of invitation history unnecessarily. Open Filters and choose a club name, activity or sport, status, days, or an invitation date range before searching.'
      }
    ];
  }

  return [
    {
      id: 'club-previous-filtered',
      tone: cards.length > 0 ? 'positive' : 'warning',
      title: cards.length
        ? `${cards.length} archived ${cards.length === 1 ? 'invitation matches' : 'invitations match'} on this page`
        : 'No previous-year invitations match these filters',
      text: cards.length
        ? `${summaries.join(' ')}${remaining > 0 ? ` ${remaining} more are shown on this page.` : ''}`
        : 'Try widening the invitation date range, removing a status or activity filter, or searching a different club name.'
    }
  ];
}

function buildClubInvitationActions(view: string, history: any): PageHelpAction[] {
  const actions: PageHelpAction[] = [];

  if (view === 'current') {
    const reviewButton = (Array.from(document.querySelectorAll('.bClubBookingCard button')) as HTMLButtonElement[])
      .filter(isVisible)
      .find(button => /^(Book|Info)$/i.test(visibleText(button)));
    if (reviewButton) {
      const card = reviewButton.closest('.bClubBookingCard') as HTMLElement | null;
      const clubName = visibleText(card && card.querySelector('.ConsentCard__title')) || 'invitation';
      actions.push({
        id: 'club-review-invitation',
        label: `Review ${clubName}`,
        description: 'Open the booking information or form. Nothing is confirmed until you explicitly submit it.',
        run: () => reviewButton.click()
      });
    }
    actions.push({
      id: 'club-open-past-current',
      label: 'Open Past this year',
      description: 'Review recently accepted or declined invitations and any response-change options still available.',
      run: () => history.push('/clubBookings/history')
    });
  } else if (view === 'past-this-year') {
    const changeButton = (Array.from(document.querySelectorAll('.bClubBookingCard button')) as HTMLButtonElement[])
      .filter(isVisible)
      .find(button => /^(Edit response|Return to inbox)$/i.test(visibleText(button)));
    if (changeButton) {
      actions.push({
        id: 'club-change-response',
        label: visibleText(changeButton),
        description:
          'Open the available response-change flow for this card. The change is not saved until you explicitly confirm it.',
        run: () => changeButton.click()
      });
    }
    actions.push({
      id: 'club-open-inbox',
      label: 'Open Invitations',
      description: 'Return to club invitations that are currently waiting for a response.',
      run: () => history.push('/clubBookings/inbox')
    });
  } else {
    actions.push({
      id: 'club-open-inbox',
      label: 'Open Invitations',
      description: 'Return to club invitations that are currently waiting for a response.',
      run: () => history.push('/clubBookings/inbox')
    });
  }

  return actions;
}

function buildClubInvitationAnchors(view: string): PageHelpAnchor[] {
  const actionDescription =
    view === 'current'
      ? 'Book or review the invitation, or decline it while the response window remains open.'
      : view === 'past-this-year'
      ? 'Edit response or Return to inbox appears only where a recent response can still be changed.'
      : 'Previous-year cards are historical records and are opened for review.';

  return [
    {
      id: 'club-invitation-tabs',
      label: 'Club sections',
      description: 'Move between current Invitations, Browse & book, Past this year and Past previous years.',
      selector: '.parentPageHeader__tabs'
    },
    {
      id: 'club-invitation-controls',
      label: view === 'previous-years' ? 'Archive search and filters' : 'Search and filters',
      description:
        view === 'previous-years'
          ? 'Set archive filters before searching old academic years; this keeps the request focused and fast.'
          : 'Narrow the visible invitations by club, activity, date, status or day.',
      selector: '.studentHubControls, .parentListControls'
    },
    {
      id: 'club-invitation-filter-panel',
      label: 'Detailed filters',
      description: 'Apply the selected criteria to refresh the club invitation results.',
      selector: '.parentFilterSurface, .parentFilterDrawer, .ParentFilterCard'
    },
    {
      id: 'club-invitation-cards',
      label: view === 'current' ? 'Invitations waiting for you' : 'Club invitation history',
      description:
        view === 'current'
          ? 'Each card shows the club, response status, schedule, venue and any reply deadline.'
          : 'Each card shows the recorded response and whether a change is still available.',
      selector: '.parentListGrid, .studentClubInvitesContent .row, .studentHubEmpty, .parentListEmpty'
    },
    {
      id: 'club-invitation-card-actions',
      label: 'Available card action',
      description: actionDescription,
      selector: '.bClubBookingCard .clubInviteCard__footer'
    },
    {
      id: 'club-invitation-pagination',
      label: 'Result pages',
      description: 'Move through the filtered results when more than one page is available.',
      selector: '.bPagination--modern'
    }
  ];
}

function visibleElements(selector: string): HTMLElement[] {
  return (Array.from(document.querySelectorAll(selector)) as HTMLElement[]).filter(isVisible);
}

function shortCardList(cards: HTMLElement[], getSummary: (card: HTMLElement) => string): string {
  const summaries = cards
    .slice(0, 3)
    .map(getSummary)
    .filter(Boolean);
  const remaining = cards.length - summaries.length;
  return `${summaries.join(' ')}${remaining > 0 ? ` ${remaining} more are shown on this page.` : ''}`.trim();
}

function buildConsentObservations(view: string, hasAppliedArchiveFilters: boolean): PageHelpObservation[] {
  const cards = visibleElements('.consentInviteCard');
  const summary = shortCardList(cards, card => {
    const event = visibleText(card.querySelector('h3')) || 'Consent request';
    const child = visibleText(card.querySelector('.consentInviteCard__identity span'));
    const status = visibleText(card.querySelector('.consentInviteCard__identity strong'));
    const deadline = visibleText(card.querySelector('.consentInviteCard__deadline'));
    return `${event}${child ? ` for ${child}` : ''}${status ? ` — ${status}` : ''}${deadline ? `; ${deadline}` : ''}.`;
  });

  if (view === 'current') {
    return cards.length
      ? [
          {
            id: 'consent-current-items',
            tone: 'warning',
            title: `${cards.length} ${
              cards.length === 1 ? 'consent request needs' : 'consent requests need'
            } a response`,
            text: summary
          },
          {
            id: 'consent-current-rule',
            tone: 'neutral',
            title: 'Accept or decline while the response window is open',
            text:
              'A reply deadline takes priority. If no deadline was set, you can respond until the event starts. Once that point passes, the request belongs in Past this year.'
          }
        ]
      : [
          {
            id: 'consent-current-empty',
            tone: 'positive',
            title: 'No consent response is waiting for you',
            text:
              'Recently answered requests and requests whose deadline or event start has passed are in Past this year. Previous academic years are in Past previous years.'
          }
        ];
  }

  if (view === 'past-this-year') {
    const changeButtons = visibleElements('.consentInviteCard button').filter(button =>
      /^(Accept|Edit response|Cancel consent|Return to inbox)$/i.test(visibleText(button))
    );
    return [
      {
        id: 'consent-past-purpose',
        tone: cards.length ? 'neutral' : 'positive',
        title: cards.length
          ? `${cards.length} recent ${cards.length === 1 ? 'consent record is' : 'consent records are'} shown`
          : 'No earlier consent records are shown for this academic year',
        text: cards.length
          ? summary
          : 'Clear or change the filters if you expected to find a recent response or expired request.'
      },
      ...(cards.length
        ? [
            {
              id: 'consent-past-change',
              tone: changeButtons.length ? ('positive' as const) : ('warning' as const),
              title: changeButtons.length
                ? `${changeButtons.length} ${
                    changeButtons.length === 1 ? 'response can' : 'responses can'
                  } still be changed`
                : 'The responses shown here are read-only',
              text: changeButtons.length
                ? 'Use the action on the relevant card. Review the form carefully; nothing changes until you explicitly confirm it.'
                : 'When no response action appears, the deadline or event start has passed. Contact the school directly if an important correction is needed.'
            }
          ]
        : [])
    ];
  }

  if (!hasAppliedArchiveFilters) {
    return [
      {
        id: 'consent-archive-filter',
        tone: 'warning',
        title: 'Use filters to search previous academic years',
        text:
          'Set an event date range and, if useful, a child, sport, event or response status. Filtering first keeps this long-term archive fast and avoids downloading unrelated history.'
      }
    ];
  }
  return [
    {
      id: 'consent-archive-results',
      tone: cards.length ? 'positive' : 'warning',
      title: cards.length
        ? `${cards.length} archived ${
            cards.length === 1 ? 'consent record matches' : 'consent records match'
          } on this page`
        : 'No archived consent records match these filters',
      text: cards.length
        ? summary
        : 'Widen the event date range or remove one of the child, sport, event or status filters.'
    }
  ];
}

function buildAvailabilityObservations(
  view: string,
  audience: Audience,
  hasAppliedArchiveFilters: boolean
): PageHelpObservation[] {
  const cards = visibleElements('.availabilityCard, .bAvailabilityReportCard');
  const summary = shortCardList(cards, card => {
    const event = visibleText(card.querySelector('.availabilityCard__title')) || 'Availability report';
    const child = visibleText(card.querySelector('.ConsentCard__student'));
    const status = visibleText(card.querySelector('.ConsentCard__status'));
    const sender = visibleText(card.querySelector('.availabilityCard__sender'));
    return `${event}${child ? ` for ${child}` : ''}${status ? ` — ${status}` : ''}${sender ? `; ${sender}` : ''}.`;
  });
  const period = view === 'previous-years' ? 'previous academic years' : 'the current academic year';

  if (view === 'previous-years' && !hasAppliedArchiveFilters) {
    return [
      {
        id: 'availability-previous-filter-required',
        tone: 'warning',
        title: 'Set filters to search previous academic years',
        text:
          'Choose an event date range and any useful activity, participant or response filters. The archive waits for filters so it does not download years of history unnecessarily.'
      }
    ];
  }

  return [
    {
      id: `availability-${view}-purpose`,
      tone: cards.length ? 'neutral' : 'positive',
      title: cards.length
        ? `${cards.length} availability ${cards.length === 1 ? 'report is' : 'reports are'} shown`
        : `No availability reports are shown for ${period}`,
      text: cards.length
        ? summary
        : view === 'previous-years'
        ? 'Use an event date range and the other filters to find an older response.'
        : 'A report appears here after you submit availability from an upcoming event.'
    },
    {
      id: 'availability-how-to-report',
      tone: 'neutral',
      title: 'Availability is reported from the event page',
      text: `Open an upcoming event and use Report availability. It appears only before the event starts and when the school allows the ${
        audience === 'parent' ? 'parent' : 'student'
      } role to report.${
        audience === 'parent' ? ' Sent by identifies whether the parent or the student submitted each response.' : ''
      }`
    }
  ];
}

function buildAbsenceObservations(view: string, hasAppliedArchiveFilters: boolean): PageHelpObservation[] {
  const cards = visibleElements('.bAbsenceRequestCard');
  const summary = shortCardList(cards, card => {
    const child = visibleText(card.querySelector('.bAbsenceRequestCard__identity strong')) || 'Child';
    const status = visibleText(card.querySelector('.bAbsenceRequestCard__status'));
    const date = visibleText(card.querySelector('.bAbsenceRequestCard__date'));
    const details = visibleText(card.querySelector('.bAbsenceRequestCard__chips'));
    return `${child}${date ? ` — ${date}` : ''}${status ? `, ${status}` : ''}${details ? `; ${details}` : ''}.`;
  });

  if (view !== 'current' && !hasAppliedArchiveFilters) {
    return [
      {
        id: 'absence-archive-filter-required',
        tone: 'warning',
        title: 'Set filters to search the absence archive',
        text:
          'Choose an absence date range and, if useful, a child or details filter. The archive waits for filters so older records are not downloaded unnecessarily.'
      }
    ];
  }

  return [
    {
      id: `absence-${view}-records`,
      tone: cards.length ? 'neutral' : 'positive',
      title: cards.length
        ? `${cards.length} absence ${cards.length === 1 ? 'notification is' : 'notifications are'} shown`
        : view === 'current'
        ? 'No current-year absence notifications are shown'
        : 'No archived absence notifications match this search',
      text: cards.length
        ? summary
        : view === 'current'
        ? 'Use New absence request when you need to tell a child’s school about an absence.'
        : 'Change the child, activity, reason or date filters to search older notifications.'
    },
    {
      id: 'absence-purpose',
      tone: 'neutral',
      title: 'This sends a notification to the selected child’s school',
      text:
        'Choose the child first because linked schools can have different settings. Submitted notifications can be reviewed here; only the school can record whether an absence is authorised.'
    }
  ];
}

function buildAwardsObservations(audience: Audience): PageHelpObservation[] {
  const cards = visibleElements('.AwardCard');
  const summary = shortCardList(cards, card => {
    const award = visibleText(card.querySelector('h6')) || 'Award';
    const child = visibleText(card.querySelector('.awardCardSummary__child strong'));
    const points = visibleText(card.querySelector('.ac-points-chip'));
    const by = visibleText(card.querySelector('.awardCardMeta__row strong'));
    return `${award}${child ? ` for ${child}` : ''}${points ? ` — ${points}` : ''}${by ? `, awarded by ${by}` : ''}.`;
  });
  return [
    {
      id: 'awards-visible',
      tone: cards.length ? 'positive' : 'neutral',
      title: cards.length
        ? `${cards.length} ${cards.length === 1 ? 'award is' : 'awards are'} shown for the current academic year`
        : 'No current-year awards match this selection',
      text: cards.length
        ? summary
        : audience === 'parent'
        ? 'Choose another child or clear the filters. Awards can differ by school because each linked school enables and issues them separately.'
        : 'Clear the filters to check the complete current-year list. Awards appear after the school issues them.'
    },
    {
      id: 'awards-read-only',
      tone: 'neutral',
      title: 'Awards and house points are read-only here',
      text:
        'Open a card to see the full reason, awarding member of staff and gallery. Only the school can issue or change recognition.'
    }
  ];
}

function buildChildrenObservations(): PageHelpObservation[] {
  const cards = visibleElements('.parentChildCard');
  if (!cards.length) {
    return [
      {
        id: 'children-none',
        tone: 'warning',
        title: 'No children are linked to this parent role',
        text: 'Request the required parent access from Profile, or contact the school if an approved child is missing.'
      }
    ];
  }
  const summary = shortCardList(cards, card => {
    const name = visibleText(card.querySelector('.parentChildCard__identity h2')) || 'Child';
    const chips = visibleText(card.querySelector('.parentChildCard__chips'));
    const counts = visibleText(card.querySelector('.parentChildCard__actions'));
    return `${name}${chips ? ` — ${chips}` : ''}${counts ? `; ${counts}` : ''}.`;
  });
  return [
    {
      id: 'children-linked',
      tone: 'positive',
      title: `${cards.length} linked ${cards.length === 1 ? 'child' : 'children'}`,
      text: summary
    },
    {
      id: 'children-shortcuts',
      tone: 'neutral',
      title: 'Each shortcut is already filtered to that child',
      text:
        'Calendar opens their events; Awards opens current recognition; Consents opens requests that can still be answered. School, form and house chips appear only when those values are supplied.'
    }
  ];
}

function buildProgrammeObservations(audience: Audience): PageHelpObservation[] {
  const cards = visibleElements('.parentClubProgrammeCard, .studentProgrammeCard');
  const summary = shortCardList(cards, card => {
    const name = visibleText(card.querySelector('h2')) || 'Club programme';
    const windowText = visibleText(
      card.querySelector('.parentClubProgrammeCard__window, .studentProgrammeCard__window')
    );
    const state = visibleText(
      card.querySelector('.parentClubProgrammeCard__state, .studentProgrammeCard__head > span')
    );
    return `${name}${state ? ` — ${state}` : ''}${windowText ? `; ${windowText}` : ''}.`;
  });
  return [
    {
      id: 'programmes-visible',
      tone: cards.length ? 'neutral' : 'positive',
      title: cards.length
        ? `${cards.length} club ${cards.length === 1 ? 'programme is' : 'programmes are'} available to review`
        : 'No club programmes are currently available for this role',
      text: cards.length
        ? summary
        : 'A programme appears when the school publishes it to this role and the linked participant is eligible.'
    },
    {
      id: 'programmes-availability',
      tone: 'neutral',
      title: 'Opening a programme is not the same as booking it',
      text: `Availability depends on the booking window, capacity and eligibility.${
        audience === 'parent' ? ' Select the child shown on the card before opening the booking.' : ''
      } A booking or payment is made only after you explicitly confirm it.`
    }
  ];
}

function buildBasketObservations(): PageHelpObservation[] {
  const orders = visibleElements('.parentBasketPage__orders > *');
  const total = visibleText(firstVisible('.parentBasketPage__total'));
  return orders.length
    ? [
        {
          id: 'basket-items',
          tone: 'warning',
          title: `${orders.length} ${orders.length === 1 ? 'booking is' : 'bookings are'} waiting in your basket`,
          text: `${total ? `${total}. ` : ''}Review the child, club, price and form information before checkout.`
        },
        {
          id: 'basket-checkout',
          tone: 'neutral',
          title: 'Checkout is an explicit payment action',
          text:
            'A place or price can change before checkout. Squady can explain the basket but will never remove an item or start payment for you.'
        }
      ]
    : [
        {
          id: 'basket-empty',
          tone: 'positive',
          title: 'Your club booking basket is empty',
          text: 'Open Browse club programmes to choose an eligible activity and participant.'
        }
      ];
}

function buildEventObservations(audience: Audience): PageHelpObservation[] {
  const name = visibleText(firstVisible('.schoolEventPage__backRow strong')) || 'This event';
  const hero = visibleText(firstVisible('.eventReadOnlyHero, .schoolEventMainInformation, .schoolEventPage__header'));
  const activeTab = visibleText(
    firstVisible('[role="tab"][aria-selected="true"], .bTabs .active, .eSchoolEventTab.mActive')
  );
  const report = findButton(/report availability/i);
  return [
    {
      id: 'event-current',
      tone: 'neutral',
      title: name,
      text:
        hero && hero !== name
          ? hero.slice(0, 420)
          : 'Use the visible tabs to review the information available to this role.'
    },
    {
      id: 'event-active-tab',
      tone: 'neutral',
      title: activeTab ? `${activeTab} is selected` : 'Only relevant event sections are shown',
      text:
        'Details with no values are hidden. Parent Consent, On the day jobs and Recognition appear only when they apply to a linked child; the student sees the equivalent information for themselves.'
    },
    {
      id: 'event-availability-status',
      tone: report && !report.disabled ? 'positive' : 'neutral',
      title:
        report && !report.disabled
          ? 'You can report availability for this event'
          : 'Availability cannot be reported from this event now',
      text:
        report && !report.disabled
          ? 'Open the form to review the response. Nothing is sent until you explicitly submit it.'
          : `The event may have started, or the school may not allow the ${audience} role to report availability for it.`
    }
  ];
}

function buildProfileObservations(): PageHelpObservation[] {
  const active = visibleText(firstVisible('.eProfileBarItem.mProfileBarActive')) || 'Profile';
  const heading = visibleText(firstVisible('.upProfileCardTitle, .eRequestFormTitle, .upProfileHeaderTitle'));
  return [
    {
      id: 'profile-section',
      tone: 'neutral',
      title: heading || active,
      text:
        active === 'Roles'
          ? 'Role cards show the school context and linked children for parent roles, or assigned sports for coach and teacher roles.'
          : `You are editing the ${active} section. Changes are saved only when you use its explicit save or submit button.`
    },
    ...(active === 'Roles'
      ? [
          {
            id: 'profile-role-access',
            tone: 'warning' as const,
            title: 'Revoking a role removes its school access',
            text:
              'If you need that role again later, you must submit a new access request and wait for the school to approve it.'
          }
        ]
      : [])
  ];
}

function buildMessagesObservations(view: string): PageHelpObservation[] {
  const selected = visibleText(firstVisible('.mTableRowSelected, tr.table-active, .bMessageView, .bMessageDetails'));
  const path = window.location.pathname;
  const labels: { [key: string]: string } = {
    current: /\/outbox(?:\/|$)/.test(path) ? 'Sent' : 'Inbox',
    'past-this-year': 'Sent',
    'previous-years': 'Archive',
    main: 'Messages'
  };
  const label = labels[view] || 'Messages';
  return [
    {
      id: 'messages-purpose',
      tone: 'neutral',
      title: `${label}: ${selected ? 'a message is selected' : 'select a message to read it'}`,
      text: selected
        ? selected.slice(0, 420)
        : `${label} contains messages available to the active role. Use the list search and date or sender filters to locate a specific conversation.`
    },
    {
      id: 'messages-actions',
      tone: 'neutral',
      title: 'Message actions depend on the selected message',
      text:
        'Attachments, event links and reply controls appear only when the sender supplied them and this role is allowed to use them.'
    }
  ];
}

function buildSemanticObservations(
  route: RouteHelp,
  view: string,
  audience: Audience,
  hasAppliedArchiveFilters: boolean
): PageHelpObservation[] {
  switch (route.key) {
    case 'consent-requests':
      return buildConsentObservations(view, hasAppliedArchiveFilters);
    case 'availability-reports':
      return buildAvailabilityObservations(view, audience, hasAppliedArchiveFilters);
    case 'absence-requests':
      return buildAbsenceObservations(view, hasAppliedArchiveFilters);
    case 'awards':
      return buildAwardsObservations(audience);
    case 'children':
      return buildChildrenObservations();
    case 'club-programmes':
      return buildProgrammeObservations(audience);
    case 'basket':
      return buildBasketObservations();
    case 'event':
      return buildEventObservations(audience);
    case 'profile':
      return buildProfileObservations();
    case 'messages':
      return buildMessagesObservations(view);
    case 'payment-success':
      return [
        {
          id: 'payment-successful',
          tone: 'positive',
          title: 'The club booking payment completed successfully',
          text:
            'Return to the hub and review the booking status before attempting any further payment for the same place.'
        }
      ];
    case 'tournament-invitation':
      return [
        {
          id: 'tournament-response',
          tone: 'warning',
          title: 'Review the organiser, entry details and deadline before responding',
          text:
            'A response is available only while the invitation remains open. Squady will never accept, decline or withdraw an entry for you.'
        }
      ];
    default:
      return [
        {
          id: 'page-purpose',
          tone: 'neutral',
          title: route.title,
          text: route.summary
        }
      ];
  }
}

function buildSemanticActions(route: RouteHelp, view: string, audience: Audience): PageHelpAction[] {
  const actions: PageHelpAction[] = [];
  const addButton = (id: string, label: string, selector: string, description: string) => {
    const action = elementAction(id, label, selector, description);
    if (action) actions.push(action);
  };
  if (route.key === 'consent-requests') {
    if (view === 'current') {
      const reviewConsent = buttonAction(
        'consent-review-first',
        'Review first consent request',
        /^Accept$/i,
        'Open the response form for review. No response is saved until you explicitly confirm it.'
      );
      if (reviewConsent) actions.push(reviewConsent);
    } else {
      addButton(
        'consent-view-first',
        'View first consent record',
        '.consentInviteCard .consentInviteCard__details',
        'Open the recorded response and event details.'
      );
    }
  } else if (route.key === 'availability-reports') {
    addButton(
      'availability-view-first',
      'View first report',
      '.availabilityCard__footer button',
      'Open the report details.'
    );
  } else if (route.key === 'absence-requests') {
    addButton(
      'absence-view-first',
      'View first notification',
      '.bAbsenceRequestCard__footer button',
      'Open the submitted absence details.'
    );
  } else if (route.key === 'awards') {
    addButton(
      'award-open-first',
      'Open first award',
      '.AwardCard',
      'Open its reason, points, awarding staff member and gallery.'
    );
  } else if (route.key === 'children') {
    addButton(
      'child-open-calendar',
      'Open first child’s calendar',
      '.parentChildCard__actions button',
      'Open the calendar already filtered to this child.'
    );
  } else if (route.key === 'club-programmes') {
    addButton(
      'programme-review-first',
      'Review first programme',
      '.parentClubProgrammeCard button:not(:disabled), .studentProgrammeCard__footer button:not(:disabled)',
      `Open the programme for the selected ${
        audience === 'parent' ? 'child' : 'student'
      }. This does not complete a booking.`
    );
  } else if (route.key === 'event') {
    const report = buttonAction(
      'event-report-availability',
      'Report availability',
      /Report availability/i,
      'Open the event availability form. Nothing is sent until you explicitly submit it.'
    );
    if (report) actions.push(report);
  } else if (route.key === 'payment-success') {
    addButton('payment-back', 'Back to Parent Hub', '.parentPaymentSuccess__card button', 'Return to the parent hub.');
  }
  return actions;
}

function buildSemanticAnchors(route: RouteHelp, view: string): PageHelpAnchor[] {
  const common: PageHelpAnchor[] = [
    {
      id: `${route.key}-header`,
      label: 'Page context',
      description: route.subtitle,
      selector: '.parentPageHeader, .schoolEventPage__header, .upProfileHeaderShell'
    }
  ];
  const byRoute: { [key: string]: PageHelpAnchor[] } = {
    'consent-requests': [
      {
        id: 'consent-views',
        label: 'Current and past consent',
        description: 'Current is actionable; past views preserve response history.',
        selector: '.parentPageHeader__tabs'
      },
      {
        id: 'consent-controls',
        label: 'Child, search and filters',
        description: 'Narrow consent records by child, event, status or date.',
        selector: '.bItemsBar, .parentListControls'
      },
      {
        id: 'consent-cards',
        label: view === 'current' ? 'Requests awaiting a response' : 'Consent history',
        description: 'Each card shows the response window, event and currently available actions.',
        selector: '.parentListGrid, .parentListEmpty'
      }
    ],
    'availability-reports': [
      {
        id: 'availability-views',
        label: 'Academic-year views',
        description: 'Current-year reports and older archived responses are kept separate.',
        selector: '.parentPageHeader__tabs'
      },
      {
        id: 'availability-controls',
        label: 'Person, search and filters',
        description: 'Narrow reports by participant, activity, status or event date.',
        selector: '.bItemsBar, .parentListControls, .studentHubControls'
      },
      {
        id: 'availability-cards',
        label: 'Submitted availability',
        description: 'Cards show the response, event and who sent it.',
        selector: '.parentListGrid, .studentHubGrid, .parentListEmpty, .studentHubEmpty'
      }
    ],
    'absence-requests': [
      {
        id: 'absence-child',
        label: 'Child selection',
        description: 'The selected child determines which school receives a new notification.',
        selector: '.bItemsBar'
      },
      {
        id: 'absence-controls',
        label: 'Search and filters',
        description: 'Narrow notifications by activity, reason, decision or date.',
        selector: '.parentListControls'
      },
      {
        id: 'absence-cards',
        label: 'Absence notifications',
        description: 'Review the child, date, reason and school decision.',
        selector: '.parentListGrid, .parentListEmpty'
      }
    ],
    awards: [
      {
        id: 'awards-person',
        label: 'Student selection',
        description: 'Parents can choose one child or combine linked children.',
        selector: '.bItemsBar'
      },
      {
        id: 'awards-controls',
        label: 'Award filters',
        description: 'Narrow recognition by award, staff member, house, points or date.',
        selector: '.parentListControls, .awardsFilterPanel'
      },
      {
        id: 'awards-cards',
        label: 'Recognition cards',
        description: 'Open a card for its complete details and gallery.',
        selector: '.awardsListCard'
      }
    ],
    children: [
      {
        id: 'children-cards',
        label: 'Linked children',
        description: 'School details and child-filtered shortcuts are grouped on each card.',
        selector: '.parentChildrenGrid, .parentChildrenPage__empty'
      }
    ],
    'club-programmes': [
      {
        id: 'programme-cards',
        label: 'Bookable club programmes',
        description: 'Each card shows its booking window, eligibility and participant choice.',
        selector: '.parentClubProgrammeGrid, .studentProgrammeGrid, .parentListEmpty, .studentHubEmpty'
      }
    ],
    basket: [
      {
        id: 'basket-bookings',
        label: 'Bookings in basket',
        description: 'Review participant and club details before checkout.',
        selector: '.parentBasketPage__orders, .parentBasketPage__empty'
      },
      {
        id: 'basket-summary',
        label: 'Order total and checkout',
        description: 'Checkout begins the explicit secure payment flow.',
        selector: '.parentBasketPage__summary'
      }
    ],
    event: [
      {
        id: 'event-summary',
        label: 'Event summary',
        description: 'Date, time, venue, activity details, navigation and availability action.',
        selector: '.schoolEventPage__header'
      },
      {
        id: 'event-sections',
        label: 'Event sections',
        description: 'Only tabs containing relevant information or permitted actions are shown.',
        selector: '.schoolEventPage__workspace'
      }
    ],
    profile: [
      {
        id: 'profile-navigation',
        label: 'Profile sections',
        description: 'Move between personal details, roles, requests, verification and account settings.',
        selector: '.bProfileBar'
      },
      {
        id: 'profile-content',
        label: 'Current profile section',
        description: 'Review this section before explicitly saving any changes.',
        selector: '.upProfileShellContent'
      }
    ],
    messages: [
      {
        id: 'message-list',
        label: 'Message list',
        description: 'Select a message to read its content and available actions.',
        selector: 'table, .bTable'
      },
      {
        id: 'message-details',
        label: 'Selected message',
        description: 'Content, attachments, event links and permitted actions for the selected message.',
        selector: '.bMessageView, .bMessageDetails, .bGenericContentColumn'
      }
    ]
  };
  return [...common, ...(byRoute[route.key] || [])].filter(anchor => Boolean(firstVisible(anchor.selector || '')));
}

function buildAnchors(route: RouteHelp, audience: Audience, pathname: string): PageHelpAnchor[] {
  const candidates: PageHelpAnchor[] = [
    ...(route.key === 'home' ? buildHomeAnchors(audience) : []),
    ...(route.key === 'calendar' ? buildCalendarAnchors(audience) : []),
    ...(route.key === 'club-invitations' ? buildClubInvitationAnchors(getViewKey(pathname)) : []),
    ...(!['home', 'calendar', 'club-invitations'].includes(route.key)
      ? buildSemanticAnchors(route, getViewKey(pathname))
      : []),
    {
      id: 'role-page-header',
      label: 'Page summary',
      description: 'The current screen, active section and key totals.',
      selector: '.parentPageHeader'
    },
    {
      id: 'role-page-tabs',
      label: 'Views',
      description: 'Switch between current and historical sections.',
      selector: '.parentPageHeader__tabs'
    },
    {
      id: 'role-person-picker',
      label: 'Person selection',
      description: 'Choose one linked child or combine all children.',
      selector: '.pcal-kids, .bItemsBar__mobileWrapper'
    },
    {
      id: 'role-list-controls',
      label: 'Search, filters and pages',
      description: 'Narrow the current list and move between result pages.',
      selector: '.parentListControls, .studentHubControls'
    },
    {
      id: 'role-filters',
      label: 'Open filters',
      description: 'The detailed filters currently available for this list.',
      selector: '.parentFilterSurface, .parentFilterDrawer, .awardsFilterPanel, .ParentFilterCard'
    },
    {
      id: 'role-results',
      label: 'Results',
      description: 'The records currently visible after role, date and filter rules.',
      selector: '.parentListGrid, .studentHubGrid, .awardsListCard, .childrenGrid, table'
    },
    {
      id: 'role-dialog',
      label: 'Open dialog',
      description: 'Review this dialog carefully before continuing or submitting.',
      selector: '.modal.show .modal-content'
    }
  ];
  return candidates.filter(
    anchor =>
      !(route.key === 'calendar' && anchor.id === 'role-person-picker') &&
      !(
        route.key === 'club-invitations' &&
        ['role-page-tabs', 'role-list-controls', 'role-filters', 'role-results'].includes(anchor.id)
      ) &&
      Boolean(firstVisible(anchor.selector || ''))
  );
}

function syncGeneratedAnchorAttributes(definition: PageHelpDefinition): void {
  document.querySelectorAll('[data-squady-generated-anchor="true"]').forEach(element => {
    element.removeAttribute('data-help-anchor');
    element.removeAttribute('data-squady-generated-anchor');
  });

  (definition.anchors || []).forEach(anchor => {
    if (!anchor.selector) return;
    const element = firstVisible(anchor.selector);
    if (!element || element.hasAttribute('data-help-anchor')) return;
    element.setAttribute('data-help-anchor', anchor.id);
    element.setAttribute('data-squady-generated-anchor', 'true');
  });
}

function buildDefinition(props: Props): PageHelpDefinition {
  const { audience, pathname, user, availableRoles, history } = props;
  const route = resolveRoute(pathname, audience);
  const permissions = getPermissions(availableRoles, audience);
  const pagination = getPagination();
  const filters = getFilters(pathname);
  const pathView = getViewKey(pathname);
  const visibleActiveView = visibleText(firstVisible('.parentPageHeader__tabs button.is-active'))
    .replace(/\d+\s*$/, '')
    .trim();
  const activeViewLabels: { [view: string]: string } = {
    current: 'Current',
    'past-this-year': 'Past this year',
    'previous-years': 'Past previous years'
  };
  const activeView = visibleActiveView || (pathView === 'main' ? undefined : activeViewLabels[pathView]);
  const modalTitle = visibleText(firstVisible('.modal.show .modal-title'));
  const visibleHeading = visibleText(firstVisible('.parentPageHeader h1, .bGenericContentColumn h1'));
  const observations: PageHelpObservation[] = [];
  const isHome = route.key === 'home';
  const isCalendar = route.key === 'calendar';
  const isClubInvitations = route.key === 'club-invitations';
  const hasAppliedArchiveFilters = new URLSearchParams(window.location.search || '').has('filter');

  if (isHome) observations.push(...buildHomeObservations(audience));
  if (isCalendar) observations.push(...buildCalendarObservations(audience));
  if (isClubInvitations) {
    observations.push(...buildClubInvitationObservations(pathView, audience, hasAppliedArchiveFilters));
  }
  if (!isHome && !isCalendar && !isClubInvitations) {
    observations.push(...buildSemanticObservations(route, pathView, audience, hasAppliedArchiveFilters));
  }

  if (filters.length > 0 && !isHome && !isCalendar) {
    observations.push({
      id: 'applied-filters',
      tone: 'neutral',
      title: `${filters.length} ${filters.length === 1 ? 'filter is' : 'filters are'} applied`,
      text: `${filters
        .slice(0, 4)
        .map(filter => `${filter.label}: ${filter.value}`)
        .join('; ')}. Clear or widen them if an expected record is missing.`
    });
  }

  if (route.availability === 'awards') {
    const enabled = isAwardsEnabled(permissions, user);
    observations.push({
      id: 'awards-module',
      tone: enabled ? 'positive' : 'warning',
      title: enabled ? 'Awards is enabled' : 'Awards is not enabled for this role’s school context',
      text: enabled
        ? audience === 'parent'
          ? 'At least one school linked to this parent role has an active or trial Awards module.'
          : 'The active school has an active or trial Awards module.'
        : 'A school administrator must enable the Awards module. Switching to another linked school or child may expose awards there.'
    });
  }

  if (route.availability === 'absence') {
    const enabled = isAbsenceEnabled(permissions, user);
    observations.push({
      id: 'absence-module',
      tone: enabled ? 'positive' : 'warning',
      title: enabled ? 'Absence requests are enabled' : 'Absence requests are not enabled',
      text: enabled
        ? 'At least one linked school accepts absence requests. Choose the relevant child before creating one.'
        : 'The child’s school must enable absence requests. This setting cannot be changed from a parent account.'
    });
  }

  if (route.availability === 'availability') {
    const reportButton = findButton(/report availability/i);
    observations.push({
      id: 'availability-action',
      tone: reportButton && !reportButton.disabled ? 'positive' : 'neutral',
      title:
        reportButton && !reportButton.disabled
          ? 'Availability can be reported here'
          : 'No availability action is currently offered here',
      text:
        reportButton && !reportButton.disabled
          ? 'Opening the form is safe; the report is not sent until you explicitly submit it.'
          : 'Open an upcoming event and check its settings. The event must not have started and this role must be allowed to report.'
    });
  }

  const schoolNames = Array.from(
    new Set(
      permissions
        .map(permission => String((permission && permission.school && permission.school.name) || '').trim())
        .filter(Boolean)
    )
  );
  if (audience === 'parent' && schoolNames.length > 1) {
    observations.push({
      id: 'role-context',
      tone: 'neutral',
      title: `This page combines ${schoolNames.length} schools`,
      text: 'Features and available actions are evaluated separately for each linked child and school.'
    });
  }

  if (modalTitle) {
    observations.unshift({
      id: 'open-dialog',
      tone: /decline|revoke|remove|discard|delete/i.test(modalTitle) ? 'danger' : 'warning',
      title: `${modalTitle} is open`,
      text:
        'Squady can explain this dialog and point to its controls, but will not confirm a submission or destructive action for you.'
    });
  }

  const actions: PageHelpAction[] = isHome
    ? buildHomeActions(audience)
    : isCalendar
    ? buildCalendarActions()
    : isClubInvitations
    ? buildClubInvitationActions(pathView, history)
    : buildSemanticActions(route, pathView, audience);
  const filterAction = buttonAction(
    'open-filters',
    'Open filters',
    /^Filters$/i,
    'Show the detailed filters for this page.'
  );
  const clearAction = buttonAction(
    'clear-filters',
    'Clear filters',
    /^(Clear|Clear filters|Reset)$/i,
    'Remove the active filters and widen the results.'
  );
  const absenceAction = buttonAction(
    'new-absence',
    'New absence request',
    /New absence request/i,
    'Open a new request for the selected child.'
  );
  const availabilityAction = buttonAction(
    'report-availability',
    'Report availability',
    /Report availability/i,
    'Open the availability form. Nothing is sent until submission.'
  );
  [filterAction, clearAction, absenceAction, availabilityAction].forEach(action => action && actions.push(action));

  const view = getViewKey(pathname);
  if (
    !isClubInvitations &&
    view !== 'current' &&
    /\/(consentRequests|clubBookings|availabilityReports|absenceRequests)/.test(pathname)
  ) {
    const base = pathname
      .split('/')
      .slice(0, 2)
      .join('/');
    const currentSuffix = base === '/absenceRequests' ? 'outbox' : base === '/availabilityReports' ? 'outbox' : 'inbox';
    actions.push({
      id: 'open-current-view',
      label: 'Open current',
      description: 'Return to items that are current or still actionable.',
      run: () => history.push(`${base}/${currentSuffix}`)
    });
  }

  return {
    screenId: `${audience}.${route.key}.${getViewKey(pathname)}`,
    title:
      modalTitle ||
      (isHome ? (audience === 'student' ? 'Your student home' : 'Your family home') : visibleHeading || route.title),
    subtitle: modalTitle ? `${route.title} remains underneath this dialog.` : route.subtitle,
    entity: route.entity,
    state: {
      loading: Boolean(firstVisible('.bLoader, .loader, [aria-busy="true"]')),
      total: undefined,
      visibleCount: undefined,
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages,
      activeView,
      filters
    },
    anchors: buildAnchors(route, audience, pathname),
    actions,
    observations,
    sections: [
      {
        id: 'how-this-page-works',
        title: 'How this page works',
        summary: route.summary,
        items: route.items,
        anchorIds: ['role-page-header', 'role-list-controls', 'role-results']
      },
      {
        id: 'availability-and-safety',
        title: 'Availability and safe actions',
        items: [
          'What you can see depends on the active role, linked school or child, module settings and the record’s current status.',
          'Dates and deadlines can move an item from Current to a past view and remove its response action.',
          'Squady may open a safe form or page, but never submits, accepts, declines, pays, revokes or deletes on your behalf.'
        ]
      }
    ]
  };
}

function definitionSignature(definition: PageHelpDefinition): string {
  return JSON.stringify({
    screenId: definition.screenId,
    title: definition.title,
    subtitle: definition.subtitle,
    state: definition.state,
    observations: definition.observations,
    anchors: (definition.anchors || []).map(anchor => anchor.id),
    actions: (definition.actions || []).map(action => ({
      id: action.id,
      disabled: action.disabled,
      description: action.description
    }))
  });
}

export function ParentStudentPageHelp(props: Props): null {
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const signatureRef = React.useRef('');

  React.useEffect(() => {
    let timer: number | undefined;
    let cancelled = false;

    const update = () => {
      if (cancelled) return;
      const definition = buildDefinition(props);
      syncGeneratedAnchorAttributes(definition);
      const signature = definitionSignature(definition);
      if (!registrationRef.current) {
        registrationRef.current = registerPageHelpDefinition(definition);
        signatureRef.current = signature;
      } else if (signature !== signatureRef.current) {
        signatureRef.current = signature;
        registrationRef.current.update(definition);
      }
    };

    const scheduleUpdate = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(update, 120);
    };

    update();
    scheduleUpdate();
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-current']
    });
    document.addEventListener('input', scheduleUpdate, true);
    document.addEventListener('change', scheduleUpdate, true);
    document.addEventListener('click', scheduleUpdate, true);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      observer.disconnect();
      document.removeEventListener('input', scheduleUpdate, true);
      document.removeEventListener('change', scheduleUpdate, true);
      document.removeEventListener('click', scheduleUpdate, true);
      document.querySelectorAll('[data-squady-generated-anchor="true"]').forEach(element => {
        element.removeAttribute('data-help-anchor');
        element.removeAttribute('data-squady-generated-anchor');
      });
      if (registrationRef.current) registrationRef.current.unregister();
      registrationRef.current = null;
      signatureRef.current = '';
    };
  }, [props.audience, props.pathname, props.user, props.availableRoles, props.history]);

  return null;
}
